/**
 * How an entry reaches the reader route.
 *
 * The reader cannot look its subject up from the address bar: F-NAV-23 forbids
 * reading state back out of the URL, and a refresh on `/journal/entry` must not
 * resurrect an entry — least of all a *journal* entry, which may be sitting
 * behind Journal Lock. So the route is handed one instead. The Journal home
 * (P34) calls `present()` with the projection it already holds, and the shell
 * mounts the route because the same call sets the full-viewport path.
 *
 * The repository arrives in the request rather than from a context of its own,
 * for the same reason the summary route takes its stores that way: the storage
 * ports are not part of the app's `PortBundle`, and the host that read the
 * projection already holds one. Constructing a second repository here would be
 * a second connection disagreeing with the first.
 *
 * §7.3/§7.4 give Journal a three-pane composition at ≥ 900 px in which "the
 * reader never presents as a sheet" (F-JR-01). That is a placement decision the
 * Journal workspace makes: a wide host renders {@link JournalReader} directly in
 * its inspector column and never calls `present()`. This context exists for the
 * compact route, where the reader is the whole surface.
 */

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type {
  JournalAttachment,
  JournalEntryProjection,
  JournalGoal,
  JournalRecoveryItem,
} from "@ff/domain";
import type { JournalRepositoryPort } from "@ff/domain/ports";

import { useNavigation } from "../../../app-state/navigation";
import { fullViewportEntry } from "../../../shell/routeTable";

/** The route's path in `shell/routeTable.ts`. Presented, never navigated to. */
export const JOURNAL_READER_ROUTE_PATH = "/journal/entry";

/**
 * Resolved at module scope and **by name**, the way the shell resolves Ringside
 * and Settings resolves the drill library. `fullViewportEntry` throws for a path
 * the table no longer declares, so a renamed surface fails at import with the
 * path it wanted — instead of `presentFullViewport` setting a path the shell has
 * no component for and the surface silently never appearing.
 */
const PRESENTED_PATH = fullViewportEntry(JOURNAL_READER_ROUTE_PATH).path;

export interface JournalReaderPresentation {
  readonly projection: JournalEntryProjection;
  readonly liveGoal?: JournalGoal | null;
  readonly recoveries?: readonly JournalRecoveryItem[];
  readonly repository?: JournalRepositoryPort;
  readonly onEdit?: () => void;
  readonly onToggleFavorite?: (next: boolean) => Promise<boolean>;
  readonly onDelete?: () => Promise<boolean>;
  readonly onOpenMedia?: (attachment: JournalAttachment) => void;
  /** Runs after the reader is dismissed — for instance once an entry is gone. */
  readonly onDismiss?: () => void;
}

export interface JournalReaderPresentationState {
  readonly presentation: JournalReaderPresentation | null;
  readonly present: (presentation: JournalReaderPresentation) => void;
  readonly dismiss: () => void;
}

const JournalReaderPresentationContext =
  createContext<JournalReaderPresentationState | null>(null);

export function JournalReaderPresentationProvider({
  children,
}: {
  readonly children?: ReactNode;
}): ReactNode {
  const navigation = useNavigation();
  const [presentation, setPresentation] = useState<JournalReaderPresentation | null>(null);

  const { presentFullViewport, dismissFullViewport } = navigation;

  const present = useCallback(
    (next: JournalReaderPresentation) => {
      // Stored before the path is set: the shell mounts the route synchronously
      // from the path, and a route that mounted first would render its empty
      // branch for one commit.
      setPresentation(next);
      presentFullViewport(PRESENTED_PATH);
    },
    [presentFullViewport],
  );

  const dismiss = useCallback(() => {
    dismissFullViewport();
    setPresentation(null);
  }, [dismissFullViewport]);

  const value = useMemo<JournalReaderPresentationState>(
    () => ({ presentation, present, dismiss }),
    [dismiss, present, presentation],
  );

  return (
    <JournalReaderPresentationContext.Provider value={value}>
      {children}
    </JournalReaderPresentationContext.Provider>
  );
}

/**
 * Null rather than a throw. A Journal workspace that renders the reader in its
 * own column legitimately has no presentation context above it, and a host that
 * cannot present is a host whose "open entry" action is simply absent — which is
 * the §8.6 shape — rather than one that crashes on mount.
 */
export function useOptionalJournalReaderPresentation(): JournalReaderPresentationState | null {
  return useContext(JournalReaderPresentationContext);
}
